import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import DiseaseCard from '@app/components/UI/DiseaseCard/DiseaseCard';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import { Resource } from '@app/objects/Resource';
import { ResourceTopic } from '@app/objects/ResourceTopic';
import '@app/scss/components/qualityOfLife.scss';

interface TopicPageProps {
	searchQuery: string | undefined;
	currentTopic?: ResourceTopic;
}

const Topics: React.FC<TopicPageProps> = ({ searchQuery, currentTopic }) => {
	const params = useParams<{ topic }>();

	const context = useItemsProviderContext<Resource>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading, pagination },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		if (currentTopic) {
			handleChange({
				text: searchQuery || '',
				offset: 0,
			});
		}
	}, [currentTopic, params.topic, searchQuery]);

	return (
		<>
			{currentTopic && currentTopic.name && (
				<Helmet>
					<title>{`${currentTopic.name} | Lap of Love`}</title>
					<meta property="og:title" content={`${currentTopic.name} | Lap of Love`} />
					<meta name="twitter:title" content={`${currentTopic.name} | Lap of Love`} />
					<meta name="description" content={currentTopic.description || undefined} />
					<meta property="og:description" content={currentTopic.description || undefined} />
					<meta name="twitter:description" content={currentTopic.description || undefined} />
				</Helmet>
			)}
			<div className="blog-page__content">
				<div className="quality">
					<div className="quality__content">
						<header className="quality__content-header">
							<div className="quality__content-header__title">
								Resources in:
								{' '}
								<h1>{currentTopic?.name}</h1>
							</div>
							<div className="quality__content-header-result-count">
								Viewing
								{' '}
								<span>{pagination.total}</span>
								{' '}
								resources
							</div>
						</header>

						<div className="quality__body">
							{!loading && items.map((resource) => (
								<DiseaseCard
									key={resource.id}
									name={resource.name}
									shortText={resource.shortText}
									url={`/resource-center/${currentTopic?.url}/${resource.url}`}
									topic={currentTopic?.name}
								/>
							))}
							{!loading && !items.length && currentTopic && (
								<div className="faq-block__dummy">
									Woof! We were not able to find a match. Please try another search.
								</div>
							)}
						</div>

						{loading && <BounceLoader />}
						{/* SSR Loader */}
						{(!loading && !currentTopic) && <BounceLoader />}

						<div className="text-center">
							<LoadMoreButton
								className="btn btn-lg btn-secondary blog-page__load-more"
								visible={!loading}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const TopicPage: React.FC<TopicPageProps> = ({ searchQuery, currentTopic }) => {
	return (
		<CustomRequestProvider>
			<HelmetProvider ignoreServerPage>
				<ItemsProvider
					type={`resourceTopics/${currentTopic?.id}/resources`}
					loadRequest={`resourceTopics/${currentTopic?.id}/resources`}
					pagination={{ pageSize: 24, total: 0 }}
				>
					<Topics searchQuery={searchQuery} currentTopic={currentTopic} />
				</ItemsProvider>
			</HelmetProvider>
		</CustomRequestProvider>
	);
};

export default TopicPage;
